import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../firebase/AuthContext';
import { clearPosterListScrollPosition } from '../utils/scrollNavigation';
import './Top.css';
import UserHeader from './UserHeader';

const Top = () => {
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfile, logout, sessionId } = useAuth();

  // タブ状態（現在のパスに基づいて設定）
  const [activeTab, setActiveTab] = useState(() => {
    if (location.pathname === '/comment-summary' || location.pathname === '/poster-summary') return 'summary';
    if (location.pathname === '/list' || location.pathname === '/poster-list') return 'list';
    if (location.pathname === '/top') return 'poster';
    return 'poster';
  });
  // デフォルトは「一般的な問いかけ」
  const [activeFilter, setActiveFilter] = useState('own');

  // Firestoreからquestionsコレクション取得（一般的な問いかけ）
  const [questions, setQuestions] = useState([]);

  // あなたへの問いかけ（パーソナライズ問いかけ）
  const [personalizedQuestions, setPersonalizedQuestions] = useState([]);
  const [personalizedLoading, setPersonalizedLoading] = useState(false);
  const [personalizedInsufficientData, setPersonalizedInsufficientData] = useState(false);
  const [personalizedFetched, setPersonalizedFetched] = useState(false);

  useEffect(() => {
    // ユーザー情報を取得（実際は認証状態をチェック）
    setUser({
      name: 'クラーケン',
      world: 'World',
      profileImage: '/img/craken2.png'
    });

    // Firestoreからquestionsコレクション取得（api.js経由）
    const fetchQuestions = async () => {
      const { getQuestions } = await import('../firebase/api');
      const questionsData = await getQuestions();
      setQuestions(questionsData);
    };
    fetchQuestions();
  }, []);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'list') {
      clearPosterListScrollPosition();
      navigate('/list');
    } else if (tab === 'summary') {
      navigate('/comment-summary');
    }
  };

  const fetchPersonalizedQuestions = async () => {
    if (!sessionId) return;
    setPersonalizedLoading(true);
    try {
      const { getPersonalizedQuestions } = await import('../firebase/api');
      const data = await getPersonalizedQuestions(sessionId);
      setPersonalizedQuestions(Array.isArray(data.questions) ? data.questions : []);
      setPersonalizedInsufficientData(Boolean(data.insufficient_data));
      setPersonalizedFetched(true);
    } catch (error) {
      console.error('パーソナライズ問いかけの取得に失敗:', error);
      setPersonalizedQuestions([]);
      setPersonalizedInsufficientData(false);
    } finally {
      setPersonalizedLoading(false);
    }
  };

  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
    if (filter === 'all' && !personalizedFetched && !personalizedLoading) {
      fetchPersonalizedQuestions();
    }
  };

  const handleCreatePoster = () => {
    // 新規投稿として遷移（質問は後で選択）
    navigate('/poster-create', {
      state: {
        isNewPost: true
      }
    });
  };

  const handleQuestionClick = (questionId) => {
    // 選択された質問と一緒にポスター作成画面に遷移
    const selectedQuestion = questions.find(q => q.id === questionId);
    navigate('/poster-create', {
      state: {
        question: selectedQuestion,
        isQuestionBased: true
      }
    });
  };

  const handlePersonalizedQuestionClick = (question) => {
    // 問いかけの元になったポスター・コメントの画面に遷移
    // posterImageUrl を渡さないとPosterDetail側で画像が表示されないため必須
    const focusDocId = question.source_post_ids?.[0];
    navigate('/poster-detail', {
      state: {
        posterId: question.poster_id,
        posterImageUrl: question.posterImageUrl || '',
        focusCommentDocId: focusDocId,
        // フォールバック表示用（本来はコメント自体に永続化された linked_question を優先表示する）
        linkedQuestionText: question.question || '',
      }
    });
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/start');
    } catch (error) {
      console.error('ログアウトエラー:', error);
    }
  };

  const handleEditProfile = () => {
    navigate('/profile-setup', { state: { editMode: true } });
  };

  if (!user) {
    return <div className="loading">読み込み中...</div>;
  }

  return (
    <div className="top-page-container">
      <div className="top-container">
        {/* ヘッダー */}
        <div className="top-header">
        <div className="user-info">
          <img src={userProfile?.iconURL || userProfile?.photoURL || '/img/icon.png'} alt="プロフィール画像" title="プロフィール編集" onClick={handleEditProfile} style={{cursor: 'pointer'}} />
          <span className="user-name">{userProfile?.displayName || userProfile?.nickname || 'ユーザー'} さん</span>
          {userProfile?.world && <span className="user-world">{userProfile.world}</span>}
        </div>
        <div className="header-icons">
          <img src="/img/graph.png" alt="みんなのサマリー" title="みんなのサマリー" className="header-icon" onClick={() => navigate('/')} style={{cursor: 'pointer'}} />
          <img src="/img/logout.png" alt="ログアウト" title="ログアウト" className="header-icon" onClick={() => { if (window.confirm('ログアウトしますか？')) handleLogout(); }} style={{cursor: 'pointer', marginLeft: '12px'}} />
        </div>
      </div>

      {/* タブメニュー */}
      <div className="list-tab-menu-wide">
        <button 
          className={`list-tab-wide ${activeTab === 'poster' ? 'active' : ''}`}
          onClick={() => handleTabChange('poster')}
        >
          ポスターを作る
        </button>
        <button 
          className={`list-tab-wide ${activeTab === 'list' ? 'active' : ''}`}
          onClick={() => handleTabChange('list')}
        >
          ポスター一覧
        </button>
        <button
          type="button"
          className={`list-tab-wide ${activeTab === 'summary' ? 'active' : ''}`}
          onClick={() => handleTabChange('summary')}
        >
          サマリー
        </button>
      </div>

      {/* 新規投稿ボタン */}
      <div className="post-button-container">
        <span>新しく投稿する</span>
        <button className="popup-icon-link" onClick={handleCreatePoster}>
          <img src="/img/document.png" alt="新規投稿" className="popup-icon" />
        </button>
      </div>

      {/* 問いかけセクション */}
      <div className="post-button-container">
        <span>問いかけてもらう</span>
      </div>
      
      {/* ナビゲーション */}
      <div className="navigation">
        <button 
          className={activeFilter === 'all' ? 'active' : ''}
          onClick={() => handleFilterChange('all')}
        >
          あなたへの問いかけ
        </button>
        <span> | </span>
        <button 
          className={activeFilter === 'own' ? 'active' : ''}
          onClick={() => handleFilterChange('own')}
        >
          一般的な問いかけ
        </button>
      </div>

      {activeFilter === 'all' ? (
        /* あなたへの問いかけ（パーソナライズ問いかけ） */
        <div className="top-personalized-questions">
          {personalizedLoading ? (
            <div className="top-personalized-loading">読み込み中...</div>
          ) : personalizedInsufficientData ? (
            <div className="top-personalized-empty">
              まだ問いかけを作れるだけのコメントが集まっていません。もう少し投稿してみてください。
            </div>
          ) : personalizedQuestions.length === 0 ? (
            <div className="top-personalized-empty">
              今はおすすめできる問いかけがありません。
            </div>
          ) : (
            <ul className="top-personalized-list">
              {personalizedQuestions.map((q, index) => (
                <li key={q.source_post_ids?.[0] || index} className="top-personalized-item">
                  <button
                    type="button"
                    className="top-personalized-card"
                    onClick={() => handlePersonalizedQuestionClick(q)}
                  >
                    <img
                      src={q.posterImageUrl || '/img/placeholder.png'}
                      alt=""
                      className="top-personalized-thumb"
                      onError={(e) => { e.target.src = '/img/placeholder.png'; }}
                    />
                    <div className="top-personalized-body">
                      <span className="top-personalized-comment-title">
                        {q.comment_title || '対象コメント'}
                      </span>
                      <span className="top-personalized-question-text">{q.question}</span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : (
        /* 質問リスト（一般的な問いかけ） */
        <ul className="top-question-list">
          {questions.map((question, index) => (
            <li key={question.id}>
              <img
                src={question.icon}
                alt="質問アイコン"
                className={`top-question-icon question-icon-${index + 1}`}
              />
              <button
                className="top-question-link"
                onClick={() => handleQuestionClick(question.id)}
              >
                {question.title}
              </button>
            </li>
          ))}
        </ul>
      )}
      </div>
    </div>
  );
};

export default Top;
